/**
 * windFieldSampler.ts — bilinear sampling over a normalized WindField.
 *
 * Port of `scripts/wind-spike/lib/field.js` sampling (D2):
 *   - bilinear interpolation over the 4 surrounding grid nodes
 *   - STRICT validity: a sample is valid only if ALL 4 corners are valid
 *     (never paints past the coverage/land boundary)
 *   - hourly time blending: u and v blended separately and continuously
 *     between the two snapshots bracketing the requested hour (no flow
 *     jumps); speed/direction derived from the blended vector
 *
 * Values are in m/s. Direction is meteorological FROM, degrees [0, 360),
 * consistent with the field contract (u = speed·sin(dir), v = speed·cos(dir)).
 * An invalid sample returns NaN components — only `valid` is meaningful
 * (missing must never read as calm-0).
 */

import type { WindField } from './windField';

export interface WindFieldSample {
  u: number; // m/s
  v: number; // m/s
  speed: number; // m/s
  speedKt: number;
  direction: number; // deg meteorological FROM, [0, 360)
  valid: boolean;
}

/** Direction (meteorological FROM) from a u/v vector, degrees [0, 360). */
export function directionFromUV(u: number, v: number): number {
  const deg = (Math.atan2(u, v) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

function invalidSample(): WindFieldSample {
  return { u: NaN, v: NaN, speed: NaN, speedKt: NaN, direction: NaN, valid: false };
}

/**
 * Sample the field at (lng, lat) at forecast hour `t` (float; whole hours
 * are exact snapshots, fractional hours blend the two bracketing ones).
 */
export function sampleWindField(
  field: WindField,
  lng: number,
  lat: number,
  t: number,
): WindFieldSample {
  const { bounds, cols, rows } = field.grid;
  if (lng < bounds.west || lng > bounds.east || lat < bounds.south || lat > bounds.north) {
    return invalidSample();
  }

  const fx = ((lng - bounds.west) / (bounds.east - bounds.west)) * (cols - 1);
  const fy = ((lat - bounds.south) / (bounds.north - bounds.south)) * (rows - 1);
  const x0 = Math.min(cols - 2, Math.max(0, Math.floor(fx)));
  const y0 = Math.min(rows - 2, Math.max(0, Math.floor(fy)));
  const tx = Math.min(1, Math.max(0, fx - x0));
  const ty = Math.min(1, Math.max(0, fy - y0));

  const hourCount = field.hours.length;
  const h0 = Math.min(hourCount - 1, Math.max(0, Math.floor(t)));
  const h1 = Math.min(hourCount - 1, h0 + 1);
  const alpha = Math.max(0, Math.min(1, t - h0));

  const { missing } = field.cells;
  const i00 = y0 * cols + x0;
  const quadValid =
    missing[i00] === 0 &&
    missing[i00 + 1] === 0 &&
    missing[i00 + cols] === 0 &&
    missing[i00 + cols + 1] === 0;
  if (!quadValid) return invalidSample();

  const { u: U, v: V } = field.levels;
  // Bilinear in space per component; then linear blend across hours —
  // u and v are blended separately so the vector direction morphs
  // continuously instead of snapping.
  const bilerp = (arr: Float32Array[], h: number): number => {
    const a0 = arr[h][i00] * (1 - tx) + arr[h][i00 + 1] * tx;
    const a1 = arr[h][i00 + cols] * (1 - tx) + arr[h][i00 + cols + 1] * tx;
    return a0 * (1 - ty) + a1 * ty;
  };

  let u = bilerp(U, h0);
  let v = bilerp(V, h0);
  if (h1 !== h0) {
    u = u * (1 - alpha) + bilerp(U, h1) * alpha;
    v = v * (1 - alpha) + bilerp(V, h1) * alpha;
  }

  const speed = Math.hypot(u, v);
  return {
    u,
    v,
    speed,
    speedKt: speed / 0.514444,
    direction: directionFromUV(u, v),
    valid: true,
  };
}

/**
 * Pure validity check: true only inside the grid box with all 4 surrounding
 * nodes valid. Used to decide whether anything may be painted at a point.
 */
export function windFieldValidAt(field: WindField, lng: number, lat: number): boolean {
  const { bounds, cols, rows } = field.grid;
  if (lng < bounds.west || lng > bounds.east || lat < bounds.south || lat > bounds.north) {
    return false;
  }
  const fx = ((lng - bounds.west) / (bounds.east - bounds.west)) * (cols - 1);
  const fy = ((lat - bounds.south) / (bounds.north - bounds.south)) * (rows - 1);
  const x0 = Math.min(cols - 2, Math.max(0, Math.floor(fx)));
  const y0 = Math.min(rows - 2, Math.max(0, Math.floor(fy)));
  const { missing } = field.cells;
  const i00 = y0 * cols + x0;
  return (
    missing[i00] === 0 &&
    missing[i00 + 1] === 0 &&
    missing[i00 + cols] === 0 &&
    missing[i00 + cols + 1] === 0
  );
}
